(function () {
  'use strict';

  const sectionHandlers = new WeakMap();

  // Scroll events carry no "ended" signal — treat this much silence as settled.
  const SCROLL_SETTLE_MS = 150;
  const AUTOPLAY_SPEED_MS = 4000;

  const initStripBanner = (section) => {
    if (!section || section.dataset.initialized === 'true') return;

    const track = section.querySelector('[data-strip-banner-track]');
    const slides = section.querySelectorAll('[data-strip-banner-slide]');
    const dots = section.querySelectorAll('[data-strip-banner-dot]');
    const totalSlides = slides.length;
    const isCarousel = !!track && totalSlides > 1;

    if (!isCarousel) {
      section.dataset.initialized = 'true';
      return;
    }

    const autoplayEnabled = section.dataset.autoSlideEnabled !== 'false';
    let currentIndex = 0;
    let autoplayTimer = null;
    let isOffScreen = false;
    let isHovering = false;
    let isUserScrolling = false;
    let isProgrammaticScroll = false;
    let scrollFrame = null;
    let settleTimer = null;

    // The track is a scroll-snap container, so slide width is whatever the
    // browser laid out — never a vw guess.
    const slideWidth = () => slides[0].clientWidth;

    const updateDots = () => {
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === currentIndex));
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      isProgrammaticScroll = true;
      currentIndex = ((index % totalSlides) + totalSlides) % totalSlides;
      track.scrollTo({ left: currentIndex * slideWidth(), behavior });
      updateDots();
    };

    const stopAutoplay = () => {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    // Every reason to stay paused is checked here, so no single event can
    // resume autoplay while another still wants it stopped.
    const startAutoplay = () => {
      if (!autoplayEnabled || isHovering || isUserScrolling || isOffScreen || document.hidden) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => scrollToIndex(currentIndex + 1), AUTOPLAY_SPEED_MS);
    };

    const onScrollSettled = () => {
      isProgrammaticScroll = false;
      if (!isUserScrolling) return;
      isUserScrolling = false;
      startAutoplay();
    };

    // Native scrolling drives the dots — trackpad, touch, and scrollTo alike.
    const onScroll = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onScrollSettled, SCROLL_SETTLE_MS);

      // A scroll we didn't start is the user swiping. Hold autoplay back so it
      // can't yank the track mid-gesture — the only pause that works on touch.
      if (!isProgrammaticScroll && !isUserScrolling) {
        isUserScrolling = true;
        stopAutoplay();
      }

      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = null;
        const index = Math.round(track.scrollLeft / slideWidth());
        if (index !== currentIndex && index >= 0 && index < totalSlides) {
          currentIndex = index;
          updateDots();
        }
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });

    section.addEventListener('mouseenter', () => {
      isHovering = true;
      stopAutoplay();
    });

    section.addEventListener('mouseleave', () => {
      isHovering = false;
      startAutoplay();
    });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        scrollToIndex(i);
        stopAutoplay();
        startAutoplay();
      });
    });

    const onResize = () => scrollToIndex(currentIndex, 'auto');

    window.addEventListener('resize', onResize);

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        isOffScreen = !entry.isIntersecting;
        if (entry.isIntersecting) {
          startAutoplay();
        } else {
          stopAutoplay();
        }
      });
    }, { threshold: 0 });

    observer.observe(section);

    const clearTimers = () => {
      clearTimeout(settleTimer);
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
    };

    sectionHandlers.set(section, {
      onResize,
      onVisibilityChange,
      stopAutoplay,
      clearTimers,
      observer,
    });

    updateDots();
    startAutoplay();
    section.dataset.initialized = 'true';
  };

  const cleanupSection = (section) => {
    const handlers = sectionHandlers.get(section);
    if (!handlers) return;
    handlers.stopAutoplay?.();
    handlers.clearTimers?.();
    if (handlers.onResize) window.removeEventListener('resize', handlers.onResize);
    if (handlers.onVisibilityChange) document.removeEventListener('visibilitychange', handlers.onVisibilityChange);
    handlers.observer?.disconnect();
    sectionHandlers.delete(section);
  };

  const initAll = () => {
    document.querySelectorAll('[data-strip-banner]:not([data-initialized="true"])').forEach(initStripBanner);
  };

  document.addEventListener('DOMContentLoaded', initAll);

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-strip-banner]');
    if (section) initStripBanner(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('[data-strip-banner]');
    if (section) cleanupSection(section);
  });
})();
